import { PanelBoardCreateComponent } from '../../creation/panel-board-create/panel-board-create.component';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { toBoardFolderStitchSummary } from '../../shared/board-stitch-summary.util';
import { AddKnotsComponent } from '@modules/knots/components/add-knots/add-knots.component';
import { AddTagsComponent } from '@modules/tags/components/add-tags/add-tags.component';
import { AddConnectionsComponent } from '@modules/connections/components/add-connections/add-connections.component';
import { PostponeContextMenuComponent } from '@modules/common/components/postpone-context-menu/postpone-context-menu.component';
import { injectFollowUpWording } from '@modules/common/utils/follow-up-wording';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { CLOSE_POPOVER } from '@frontendlib/components/features/popover/types/close-popover.injection-token';
import { ContextMenuDividerComponent } from '@frontendlib/components/features/popover/components/context-menu-divider/context-menu-divider.component';
import { ContextMenuItemComponent } from '@frontendlib/components/features/popover/components/context-menu-item/context-menu-item.component';
import { SimpleInputComponent } from '@modules/form-controls/components/simple-input/simple-input.component';
import { BoardActionsService } from '../../../services/board-actions.service';
import { BoardListStore } from '../../../services/board-list.store';
import { BoardsStore } from '../../../services/boards.store';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { injectBoardOpener } from '../../shared/board-opener';
import { type BoardFolder } from '../../../types/board';
import { titleProblem } from '../../creation/boards-quick-form/boards-quick-form.component';

@Component({
  selector: 'app-boards-folder-context-menu',
  templateUrl: './boards-folder-context-menu.component.html',
  styleUrls: ['../../../../elements/components/context-menus/stitch-context-menu/stitch-context-menu.component.less'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PanelBoardCreateComponent,AddKnotsComponent, AddTagsComponent, AddConnectionsComponent, PostponeContextMenuComponent,
    Button2Component,
    ContextMenuItemComponent,
    ContextMenuDividerComponent,
    SimpleInputComponent,
  ],
})
export class BoardsFolderContextMenuComponent {
  public readonly folder = input<BoardFolder>();
  /** True requests selection of the contents after opening the folder. */
  public readonly open = output<boolean>();
  public readonly boardCreated = output<string>();
  public readonly deleted = output<void>();
  public readonly duplicated = output<string>();

  protected readonly store = inject(BoardsStore);
  private readonly actions = inject(BoardActionsService);
  private readonly listStore = inject(BoardListStore, { optional: true });
  /** Notes' notebook menu: removal sits in the submenu, and the Follow Up and Snoozed lists also offer it as its own entry. */
  protected readonly followUpWording = injectFollowUpWording();
  protected readonly inFollowUpList = computed(() => this.listStore?.view() === 'followup');
  protected readonly inSnoozedList = computed(() => this.listStore?.view() === 'snoozed');
  private readonly closePopover = inject(CLOSE_POPOVER, { optional: true });
  private readonly space = inject(SpaceContextStore);
  private readonly openBoard = injectBoardOpener();

  protected readonly summaries = computed(() => {
    const folder = this.folder();
    return folder ? [{ ...toBoardFolderStitchSummary(folder, this.space.contextId() ?? ''), itemType: StitchType.BOARD_FOLDER }] : [];
  });
  protected setState(changes: { archived?: boolean; deleted?: boolean; snoozed?: Date | null; followed?: Date | null }): void {
    const folder = this.folder();
    if (!folder) return;
    if (changes.archived === true || changes.deleted === true) this.actions.changeFolderState(folder, changes.deleted ? 'trash' : 'archive');
    else this.store.updateFolderState(folder.id, changes);
  }
  protected selectAll(): void { const folder = this.folder(); if (folder) { this.open.emit(true); } }

  /** A folder's new board opens in the editor through the layout's opener (full window in the Boards app). */
  protected openCreated(id: string): void {
    this.boardCreated.emit(id);
    this.listStore?.ensureVisibleAfterCreate(id);
    this.close();
    this.openBoard(id);
  }

  protected readonly renameControl = new FormControl('', { nonNullable: true, validators: [Validators.required] });
  protected readonly renameSubmitted = signal(false);
  private readonly renameValue = toSignal(this.renameControl.valueChanges, { initialValue: this.renameControl.value });
  /** Why a submitted rename was refused: blank, or longer than the server accepts. */
  protected readonly renameError = computed(() => (this.renameSubmitted() ? titleProblem(this.renameValue()) : null));
  protected readonly flagLabel = computed(() => (this.folder()?.flagged ? 'Unflag' : 'Flag'));
  protected readonly pinLabel = computed(() => (this.folder()?.pinned ? 'Unpin' : 'Pin'));

  /** Seeds the rename field from the folder whenever it is (re)bound. */
  private readonly seedRename = effect(() => {
    this.renameControl.setValue(this.folder()?.title ?? '');
    this.renameSubmitted.set(false);
  });

  protected close(): void {
    this.closePopover?.next();
  }

  protected handleOpen(): void {
    this.open.emit(false);
  }

  protected pin(): void {
    const folder = this.folder();
    if (folder) this.store.pinFolder(folder.id);
  }

  protected flag(): void {
    const folder = this.folder();
    if (folder) this.store.toggleFolderFlag(folder.id);
  }

  protected duplicate(): void {
    const folder = this.folder();
    if (!folder) return;
    this.store.duplicateFolder(folder.id).subscribe((id) => {
      if (id) this.duplicated.emit(id);
    });
  }

  /** A blank Save neither saves nor closes — matching the quick-create form's own "Add title" validation
   * instead of silently closing with no explanation (SBUX-988 review). */
  protected handleRename(): void {
    this.renameSubmitted.set(true);
    const title = this.renameControl.value.trim();
    if (titleProblem(title)) {
      return;
    }
    const folder = this.folder();
    if (folder) this.store.renameFolder(folder.id, title);
    this.close();
  }

  protected delete(): void {
    const folder = this.folder();
    if (folder) this.actions.deleteFolder(folder, () => this.deleted.emit());
  }
}
